import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Stack,
  Text,
  useToast,
} from "@chakra-ui/react";
import { secureFetch } from "../data/secureFetch";
import { setDonorToken } from "../data/donorAuth";

function DonorAuthModal({ isOpen, onClose, onSuccess, initialMode = "login" }) {
  const toast = useToast();
  const [mode, setMode] = useState(initialMode);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
  });

  useEffect(() => {
    if (isOpen) {
      setMode(initialMode);
    }
  }, [isOpen, initialMode]);

  const resetAndClose = () => {
    setForm({
      firstName: "",
      lastName: "",
      email: "",
      password: "",
    });
    onClose();
  };

  const submitLogin = async () => {
    setLoading(true);
    try {
      const response = await secureFetch("/api/donor-auth/login", {
        method: "POST",
        includeFirebaseAuth: false,
        headers: { "Content-Type": "application/json" },
        data: {
          email: form.email.trim(),
          password: form.password,
        },
      });
      if (!response.ok) {
        throw new Error(response.message || "Failed to sign in");
      }
      if (!response.token) {
        throw new Error("Sign in succeeded but no token was returned");
      }
      setDonorToken(response.token);
      if (typeof onSuccess === "function") {
        onSuccess(response);
      }
      toast({
        title: "Signed in",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
      resetAndClose();
    } catch (error) {
      toast({
        title: "Sign in failed",
        description: error.message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const submitRegister = async () => {
    setLoading(true);
    try {
      const response = await secureFetch("/api/donor-auth/register", {
        method: "POST",
        includeFirebaseAuth: false,
        headers: { "Content-Type": "application/json" },
        data: {
          firstName: form.firstName.trim(),
          lastName: form.lastName.trim(),
          email: form.email.trim(),
          password: form.password,
        },
      });
      if (!response.ok) {
        throw new Error(response.message || "Failed to create account");
      }
      toast({
        title: "Account created",
        description:
          response.message ||
          "Check your inbox and verify your email before signing in.",
        status: "success",
        duration: 5000,
        isClosable: true,
      });
      setMode("login");
      setForm((prev) => ({
        ...prev,
        password: "",
      }));
    } catch (error) {
      toast({
        title: "Create account failed",
        description: error.message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={resetAndClose} size="md">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          {mode === "login" ? "Donor Sign In" : "Create Donor Account"}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Stack spacing={3}>
            {mode === "register" && (
              <>
                <FormControl>
                  <FormLabel>First Name</FormLabel>
                  <Input
                    value={form.firstName}
                    onChange={(e) =>
                      setForm((prev) => ({ ...prev, firstName: e.target.value }))
                    }
                  />
                </FormControl>
                <FormControl>
                  <FormLabel>Last Name</FormLabel>
                  <Input
                    value={form.lastName}
                    onChange={(e) =>
                      setForm((prev) => ({ ...prev, lastName: e.target.value }))
                    }
                  />
                </FormControl>
              </>
            )}
            <FormControl isRequired>
              <FormLabel>Email</FormLabel>
              <Input
                type="email"
                value={form.email}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, email: e.target.value }))
                }
              />
            </FormControl>
            <FormControl isRequired>
              <FormLabel>Password</FormLabel>
              <Input
                type="password"
                value={form.password}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, password: e.target.value }))
                }
              />
            </FormControl>
          </Stack>
          <Box mt={4}>
            {mode === "login" ? (
              <Text fontSize="sm" color="gray.600">
                New donor?{" "}
                <Button
                  variant="link"
                  size="sm"
                  colorScheme="blue"
                  onClick={() => setMode("register")}
                >
                  Create account
                </Button>
              </Text>
            ) : (
              <Text fontSize="sm" color="gray.600">
                Already have an account?{" "}
                <Button
                  variant="link"
                  size="sm"
                  colorScheme="blue"
                  onClick={() => setMode("login")}
                >
                  Sign in
                </Button>
              </Text>
            )}
          </Box>
        </ModalBody>
        <ModalFooter>
          <Button mr={3} onClick={resetAndClose}>
            Close
          </Button>
          <Button
            colorScheme="orange"
            isLoading={loading}
            onClick={mode === "login" ? submitLogin : submitRegister}
          >
            {mode === "login" ? "Sign In" : "Create Account"}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

export default DonorAuthModal;
